
import * as React from "react"
import { XIcon } from "lucide-react"
import { Dialog as SheetPrimitive } from "radix-ui"

import { cn } from "@/lib/utils"

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />
}

function SheetTrigger({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />
}

function SheetClose({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />
}

function SheetPortal({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />
}

function SheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50",
        className
      )}
      {...props}
    />
  )
}

const DRAG_THRESHOLD = 80

function SheetContent({
  className,
  children,
  side = "right",
  showCloseButton = true,
  dragDisabled = false,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: "top" | "right" | "bottom" | "left"
  showCloseButton?: boolean
  dragDisabled?: boolean
}) {
  const contentRef = React.useRef<HTMLDivElement>(null)
  const closeRef = React.useRef<HTMLButtonElement>(null)
  const dragStartY = React.useRef(0)
  const dragCurrentY = React.useRef(0)
  const isDragging = React.useRef(false)

  const isBottom = side === "bottom"
  const isTop = side === "top"
  const isDraggable = (isBottom || isTop) && !dragDisabled

  const handleTouchStart = React.useCallback((e: React.TouchEvent) => {
    if (!isDraggable) return
    const target = e.target as HTMLElement
    if (
      target.closest(
        'input, textarea, button, [role="button"], select, ' +
        '[role="option"], [role="listbox"], [role="menu"], [role="menuitem"], ' +
        '[data-radix-popper-content-wrapper]'
      )
    ) {
      return
    }
    // For scrollable areas, check if scroll position allows drag
    const scrollable = target.closest('[data-no-drag]') as HTMLElement | null
    if (scrollable) {
      if (isBottom && scrollable.scrollTop > 0) return
      if (isTop && scrollable.scrollTop + scrollable.clientHeight < scrollable.scrollHeight) return
    }
    dragStartY.current = e.touches[0].clientY
    dragCurrentY.current = 0
    isDragging.current = false
  }, [isDraggable, isBottom, isTop])

  const handleTouchMove = React.useCallback((e: React.TouchEvent) => {
    if (dragStartY.current === 0) return
    const deltaY = e.touches[0].clientY - dragStartY.current
    // bottom: only drag downward (deltaY > 0), top: only drag upward (deltaY < 0)
    const validDrag = isBottom ? deltaY > 0 : deltaY < 0
    if (!validDrag) {
      dragCurrentY.current = 0
      if (contentRef.current) {
        contentRef.current.style.transform = ''
      }
      return
    }
    isDragging.current = true
    dragCurrentY.current = Math.abs(deltaY)
    if (contentRef.current) {
      contentRef.current.style.transition = 'none'
      contentRef.current.style.transform = `translateY(${deltaY}px)`
    }
  }, [isBottom])

  const handleTouchEnd = React.useCallback(() => {
    const el = contentRef.current
    if (!el) return

    if (isDragging.current && dragCurrentY.current > DRAG_THRESHOLD) {
      // Slide away smoothly, then close
      el.style.transition = 'transform 200ms ease-out'
      el.style.transform = `translateY(${isBottom ? '100%' : '-100%'})`
      el.addEventListener('transitionend', () => {
        // Disable Radix close animation to prevent bounce-back
        el.style.animationDuration = '0s'
        closeRef.current?.click()
      }, { once: true })
    } else {
      // Snap back smoothly
      el.style.transition = 'transform 200ms ease-out'
      el.style.transform = 'translateY(0)'
      el.addEventListener('transitionend', () => {
        el.style.transition = ''
        el.style.transform = ''
      }, { once: true })
    }

    dragStartY.current = 0
    dragCurrentY.current = 0
    isDragging.current = false
  }, [isBottom])

  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        ref={isDraggable ? contentRef : undefined}
        data-slot="sheet-content"
        className={cn(
          "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col gap-4 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
          side === "right" &&
            "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm",
          side === "left" &&
            "data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm",
          side === "top" &&
            "data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 h-auto border-b",
          side === "bottom" &&
            "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 h-auto border-t",
          className
        )}
        onTouchStart={isDraggable ? handleTouchStart : undefined}
        onTouchMove={isDraggable ? handleTouchMove : undefined}
        onTouchEnd={isDraggable ? handleTouchEnd : undefined}
        {...props}
      >
        {isBottom && (
          <>
            <SheetPrimitive.Close ref={closeRef} className="sr-only" />
            <div className="flex justify-center pt-2 pb-0">
              <div className="h-1.5 w-10 rounded-full bg-muted-foreground/30" />
            </div>
          </>
        )}
        {children}
        {isTop && (
          <>
            <SheetPrimitive.Close ref={closeRef} className="sr-only" />
            <div className="flex justify-center pb-2 pt-0">
              <div className="h-1.5 w-10 rounded-full bg-muted-foreground/30" />
            </div>
          </>
        )}
        {showCloseButton && !isBottom && !isTop && (
          <SheetPrimitive.Close className="ring-offset-background focus:ring-ring data-[state=open]:bg-secondary absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none">
            <XIcon className="size-4" />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Content>
    </SheetPortal>
  )
}

function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-header"
      className={cn("flex flex-col gap-1.5 p-4", className)}
      {...props}
    />
  )
}

function SheetFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn("mt-auto flex flex-col gap-2 p-4", className)}
      {...props}
    />
  )
}

function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn("text-foreground font-semibold", className)}
      {...props}
    />
  )
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  )
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
